import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Device } from '@capacitor/device';
import { isNative } from '../utils/platform';

import en from './locales/en.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import zh from './locales/zh.json';
import ru from './locales/ru.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh', label: '中文' },
  { code: 'ru', label: 'Русский' },
];

// Languages that should render times in 24-hour format.
// English is the only 12-hour default; everything else uses 24h.
const USES_24H = ['es', 'pt', 'fr', 'de', 'it', 'ja', 'ko', 'zh', 'ru'];

export function uses24HourTime(lang) {
  if (!lang) return false;
  const base = lang.toLowerCase().split('-')[0];
  return USES_24H.includes(base);
}

const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);
const STORAGE_KEY = 'smpw.lang';

// i18n is currently gated to native (iOS/Android Capacitor) only — web stays English.
// To enable on web later, remove the isNative() guards here and in LanguageSwitcher.
export const i18nEnabled = isNative();

const chain = i18n.use(initReactI18next);
if (i18nEnabled) chain.use(LanguageDetector);

chain.init({
  resources: {
    en: { translation: en },
    es: { translation: es },
    pt: { translation: pt },
    fr: { translation: fr },
    de: { translation: de },
    it: { translation: it },
    ja: { translation: ja },
    ko: { translation: ko },
    zh: { translation: zh },
    ru: { translation: ru },
  },
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_CODES,
  nonExplicitSupportedLngs: true,
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  ...(i18nEnabled
    ? {
        detection: {
          order: ['localStorage', 'navigator'],
          lookupLocalStorage: STORAGE_KEY,
          caches: ['localStorage'],
        },
      }
    : { lng: 'en' }),
});

// Keep <html lang> in sync so browser-native controls (e.g. <input type="time">)
// render with locale-appropriate formatting (24h for most locales, 12h for en).
const syncHtmlLang = (lng) => {
  if (typeof document !== 'undefined' && lng) {
    document.documentElement.lang = lng;
  }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

// On native (Capacitor), prefer the device locale over navigator.language.
// Only applied when the user hasn't explicitly picked a language yet.
// No-op on web since i18n is gated off there.
export async function syncDeviceLanguage() {
  if (!i18nEnabled) return;
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const { value } = await Device.getLanguageCode();
    if (!value) return;
    const base = value.toLowerCase().split('-')[0];
    if (SUPPORTED_CODES.includes(base) && i18n.language !== base) {
      await i18n.changeLanguage(base);
    }
  } catch {
    // Device plugin unavailable — fall back silently.
  }
}

export function setLanguage(code) {
  localStorage.setItem(STORAGE_KEY, code);
  return i18n.changeLanguage(code);
}

export default i18n;
